import type { ComponentType } from 'react';
import {
  AssetLibraryIcon,
  BillOfMaterialsIcon,
  CareLibraryIcon,
  CareUserInstructionsIcon,
  ColourLibraryIcon,
  CostingLibraryIcon,
  CostingToDoIcon,
  CostingsIcon,
  CriticalPathIcon,
  CustomModulesIcon,
  DAMIcon,
  DPPLibraryIcon,
  DesignDetailsIcon,
  FreightDutyLibraryIcon,
  LibraryGroupIcon,
  MeasurementsIcon,
  PlaceholderManagementIcon,
  PlanningIcon,
  ProductIdentifierIcon,
  ProductsIcon,
  ProgressIndicatorIcon,
  PurchaseOrderLibraryIcon,
  PurchaseOrderToDoIcon,
  PurchaseOrdersIcon,
  RangePlanIcon,
  SamplesIcon,
  SamplesToDoIcon,
  SpecTemplateLibraryIcon,
  SubmitAndDownloadIcon,
  SupplierSuggestIcon,
  SupplierToDoIcon,
  TestingIcon,
  TestingLibraryIcon,
  TestingToDoIcon,
} from '../bombiix-icon';

export interface ModuleOrderIconProps {
  size?: number;
  className?: string;
}

export type ModuleOrderIconComponent = ComponentType<ModuleOrderIconProps>;

/**
 * Maps the icon component name stored on ModuleOrderConfigs.items[].iconName to the
 * bombiix SVG component the left navigation renders, so System Master shows the same
 * glyph as the rail. Values stored in the DB are these key strings, never SVG markup.
 */
export const MODULE_ORDER_ICON_COMPONENTS: Record<string, ModuleOrderIconComponent> = {
  AssetLibraryIcon,
  BillOfMaterialsIcon,
  CareLibraryIcon,
  CareUserInstructionsIcon,
  ColourLibraryIcon,
  CostingLibraryIcon,
  CostingToDoIcon,
  CostingsIcon,
  CriticalPathIcon,
  CustomModulesIcon,
  DAMIcon,
  DPPLibraryIcon,
  DesignDetailsIcon,
  FreightDutyLibraryIcon,
  LibraryGroupIcon,
  MeasurementsIcon,
  PlaceholderManagementIcon,
  PlanningIcon,
  ProductIdentifierIcon,
  ProductsIcon,
  ProgressIndicatorIcon,
  PurchaseOrderLibraryIcon,
  PurchaseOrderToDoIcon,
  PurchaseOrdersIcon,
  RangePlanIcon,
  SamplesIcon,
  SamplesToDoIcon,
  SpecTemplateLibraryIcon,
  SubmitAndDownloadIcon,
  SupplierSuggestIcon,
  SupplierToDoIcon,
  TestingIcon,
  TestingLibraryIcon,
  TestingToDoIcon,
};

/**
 * Legacy icomoon classes still stored against dynamic items (system canvas, control
 * libraries, custom modules, DPP) on configs written before the SVG switch. Kept so those
 * rows render the new glyph without waiting on the migration.
 */
const LEGACY_ICON_CLASS_ALIASES: Record<string, string> = {
  'icon-Inside-Custom-Modules': 'CustomModulesIcon',
  'icon-Inside-Digital-Product-Passports': 'DPPLibraryIcon',
  'icon-DAM-1': 'AssetLibraryIcon',
  'icon-Lib-Colour-Pallette': 'ColourLibraryIcon',
  'icon-Lib-BOM': 'LibraryGroupIcon',
  'icon-Inside-Measurements': 'MeasurementsIcon',
  'icon-Lib-Spec-Template-Icon': 'SpecTemplateLibraryIcon',
  'icon-User-Instructions': 'CareLibraryIcon',
  'icon-care_labels': 'CareLibraryIcon',
  'icon-Lib-Custom-Modules': 'CustomModulesIcon',
  'icon-Module': 'CustomModulesIcon',
  'icon-Lib-DPP': 'DPPLibraryIcon',
  'icon-Lib-Testing': 'TestingLibraryIcon',
  'icon-Lib-Purchase-Order-Preview': 'PurchaseOrderLibraryIcon',
  'icon-Lib-costing': 'CostingLibraryIcon',
  'icon-Lib-Freight-and-duty': 'FreightDutyLibraryIcon',
};

/**
 * Resolve a stored iconName to its SVG component, accepting either a component name or a
 * known legacy icomoon class.
 * @param iconName - Value stored on the config item
 * @returns The matching component, or undefined when the value maps to no component
 */
export function getModuleOrderIconComponent(iconName?: unknown): ModuleOrderIconComponent | undefined {
  if (typeof iconName !== 'string') return undefined;
  const key = iconName.trim();
  const direct = MODULE_ORDER_ICON_COMPONENTS[key];
  if (direct) return direct;
  const alias = LEGACY_ICON_CLASS_ALIASES[key];
  return alias ? MODULE_ORDER_ICON_COMPONENTS[alias] : undefined;
}
